import { graphql } from 'react-relay';
import type { ComponentType } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { usePageState } from '@confluence/page-context';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { FlagDescription } from './FlagDescription.tsx';
// Special Flags
import { initializeInfoFlag } from './InfoFlag.tsx';
import { initializeSuccessFlag } from './SuccessFlag.tsx';
// Helpers
import { ALLOWED_FLAG_MESSAGES } from './allowedFlagConstants.ts';
import { buildEditorFlagMessage } from './buildEditorFlagMessage.tsx';
import type {
	EditorFlagsUserPersonalInformationQuery as EditorFlagsUserPersonalInformationQuery$data,
	EditorFlagsUserPersonalInformationQueryVariables as EditorFlagsUserPersonalInformationQuery$variables,
} from './__types__/EditorFlagsUserPersonalInformationQuery.ts';
import type {
	EditorFlagsContentAncestorsQuery as EditorFlagsContentAncestorsQuery$data,
	EditorFlagsContentAncestorsQueryVariables as EditorFlagsContentAncestorsQuery$variables,
} from './__types__/EditorFlagsContentAncestorsQuery.ts';
import { usePageConflictAnalytics } from './usePageConflictAnalytics.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export type EditorErrorFlagsProps = {
	errorData?: {
		[key: string]: any; // typical format is { "Error message": true } but additional data also supported
	};
	statusCode?: number;
	lastPublisher?: string;
	nextTryIn?: number;
	spaceKey?: string;
	parentPageId?: string;
	contentId?: string;
	flagData?: {
		[key: string]: any;
	};
	contentType?: string;
	collabService?: string;
	isUnpublishedDraft?: boolean;
};

export const EditorFlags: ComponentType<EditorErrorFlagsProps & WithFlagsProps> = injectIntl(
	({
		flags,
		nextTryIn,
		statusCode,
		flagData,
		errorData,
		intl: { formatMessage },
		lastPublisher,
		parentPageId,
		collabService,
		isUnpublishedDraft,
	}: EditorErrorFlagsProps & WrappedComponentProps & WithFlagsProps) => {
		const [{ contentSlug, contentId, spaceKey, contentType }] = usePageState();
		const isLivePage = useIsCurrentPageLive();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { triggerPageConflictAnalytics } = usePageConflictAnalytics({
			contentId,
			contentType,
			collabService,
			source: 'EditorFlags',
			actionSubjectId: 'editor.flag',
		});
		const userInfoQueryResult = useQueryInterop<
			EditorFlagsUserPersonalInformationQuery$data,
			EditorFlagsUserPersonalInformationQuery$variables
		>(
			graphql`
				query EditorFlagsUserPersonalInformationQuery($accountId: ID!) {
					user: confluence_atlassianUser(id: $accountId) {
						id
						displayName
					}
				}
			`,
			{
				variables: { accountId: lastPublisher || '' },
				// only make this query if the lastPublisher is defined and it is a publish/restore flag
				skip:
					!lastPublisher ||
					!errorData ||
					(!errorData[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] &&
						!errorData[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE]),
			},
		);

		// only make this query if the parentPageId is NOT defined and we have an error where we want link to the parent page
		const ancestorQuerySkipped =
			Boolean(parentPageId) ||
			!contentId ||
			!errorData ||
			!errorData[ALLOWED_FLAG_MESSAGES.PARENT_NOT_PUBLISHED];
		const ancestorQueryResult = useQueryInterop<
			EditorFlagsContentAncestorsQuery$data,
			EditorFlagsContentAncestorsQuery$variables
		>(
			graphql`
				query EditorFlagsContentAncestorsQuery($contentId: ID!) {
					content(id: $contentId, status: ["current", "draft"]) {
						nodes {
							id
							ancestors {
								id
							}
						}
					}
				}
			`,
			{
				variables: { contentId: contentId! },
				skip: ancestorQuerySkipped,
			},
		);

		const [actualParentPageId, setActualParentPageId] = useState<string | undefined>(parentPageId);

		useEffect(() => {
			// when done loading, set to the parentId that was found, or "" if not found
			if (!ancestorQuerySkipped && !ancestorQueryResult.loading) {
				setActualParentPageId(
					ancestorQueryResult.data?.content?.nodes?.[0]?.ancestors?.slice(-1)[0]?.id ||
						parentPageId ||
						'',
				);
			}
		}, [parentPageId, ancestorQueryResult, ancestorQuerySkipped]);

		// Cleanup on component unmount
		useEffect(() => () => void flags.hideAllFlags(), [flags]);

		useEffect(() => {
			if (
				!(
					errorData?.[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] ||
					errorData?.[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE] ||
					(errorData?.[ALLOWED_FLAG_MESSAGES.PARENT_NOT_PUBLISHED] &&
						actualParentPageId === undefined)
				) &&
				!flagData
			) {
				const flagContent = buildEditorFlagMessage({
					errorData,
					spaceKey,
					statusCode,
					formatMessage,
					nextTryIn,
					parentPageId: actualParentPageId,
					isUnpublishedDraft,
					isLivePage,
				});

				if (flagContent) {
					const flagId = uuid();
					void flags.showErrorFlag({
						id: flagId,
						title: flagContent.title,
						description: (
							<FlagDescription
								flags={flags}
								flagContent={flagContent}
								flagId={flagId}
								nextTryIn={nextTryIn}
							/>
						),
					});
					if (statusCode === 409) {
						triggerPageConflictAnalytics(
							Object.keys(errorData ?? {}).filter((k) =>
								k.startsWith(
									'com.atlassian.confluence.api.service.exceptions.ecosystem.ConflictException',
								),
							),
							{},
						);
					}
				} else {
					// If there is no errorData the value of flagProps is null
					// Clear the existing flags
					void flags.hideAllFlags();
				}
			}
		}, [
			isUnpublishedDraft,
			flags,
			flagData,
			nextTryIn,
			spaceKey,
			statusCode,
			errorData,
			lastPublisher,
			actualParentPageId,
			contentId,
			formatMessage,
			triggerPageConflictAnalytics,
			isLivePage,
		]);

		const showInfoFlag = useCallback(
			() => {
				const attributes = { contentType, spaceKey, contentId, contentSlug };
				return initializeInfoFlag(flags, formatMessage, attributes, isLivePage);
			},
			// Subscribe returns a new flag object each rerender causing infinite rerendering when displaying a publish flag,
			// so we are excluding it from the dep array.
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[formatMessage],
		);
		useEffect(() => {
			if (
				errorData &&
				(errorData[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH] ||
					errorData[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE])
			) {
				showInfoFlag()(userInfoQueryResult, { ...errorData });
			}
		}, [userInfoQueryResult, errorData, showInfoFlag]);

		const showSuccessFlag = useCallback(() => {
			const attributes = { contentType, spaceKey, contentId, contentSlug };
			initializeSuccessFlag(flags, formatMessage, { attributes, flagData }, createAnalyticsEvent);
			// Subscribe returns a new flag object each rerender causing infinite re-rendering,
			// so we are excluding it from the dep array.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [formatMessage]);

		useEffect(() => {
			if (flagData) {
				showSuccessFlag();
			}
		}, [flagData, showSuccessFlag]);

		return ancestorQueryResult?.error ? <ErrorDisplay error={ancestorQueryResult.error} /> : null;
	},
);

// This will replace packages/confluence-fabric-editor/src/components/EditorErrorFlags/EditorErrorFlags.ts
// Once we switch to @confluence/full-page-editor
export const EditorErrorFlags: ComponentType<Omit<EditorErrorFlagsProps, 'flags'>> =
	withFlags<EditorErrorFlagsProps>(EditorFlags);
